import React from 'react'
import {
  TextField as MuiTextField,
  styled,
  Box,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Controller, useFormContext } from 'react-hook-form'
import { tokens } from '../../styles/materialTheme'

const StyledTextField = styled(MuiTextField)({
  '.MuiInputBase-root': {
    width: '100%',
    maxWidth: '100%',
  },
  '& .MuiFormHelperText-root': { margin: '0rem' },
})

const StyledLabel = styled('label')(({ theme }) => ({
  fontFamily: 'Prelo-Black, sans-serif',
  fontSize: '1rem',
  paddingBottom: '0.5rem',
  color: theme.palette.text.primary,
  display: 'block',
}))

interface TextFieldProps {
  labelName: string
  fieldName: string
  componentName: string
  fieldRequired?: boolean
  maxLength?: number
  placeholderText?: string
  fieldValidation?: (value: string) => string | true
  disabled?: boolean
}

const TextField = ({
  labelName,
  fieldName,
  componentName,
  maxLength = 250,
  fieldRequired = false,
  placeholderText = '',
  fieldValidation = undefined,
  disabled = false,
}: TextFieldProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext()

  return (
    <Box key={fieldName}>
      <StyledLabel htmlFor={fieldName}>
        {fieldRequired ? `${t(labelName)} *` : t(labelName)}
        <span className="sr-only">{t('srOnlyRequired')}</span>
      </StyledLabel>
      <Controller
        name={fieldName}
        control={control}
        rules={{
          maxLength: {
            value: maxLength,
            message: t('MaxLengthError', { max: maxLength }),
          },
          required: fieldRequired
            ? `${t(labelName)} ${t('IsARequiredField')}`
            : false,
          validate: fieldValidation,
        }}
        render={({
          field: { onChange, value, ref },
          fieldState: { error },
        }) => (
          <StyledTextField
            id={fieldName}
            name={fieldName}
            inputRef={ref}
            data-testid={`Txt_${componentName}_${fieldName}`}
            placeholder={placeholderText}
            value={value}
            onChange={(e) => onChange(e)}
            error={!!error}
            disabled={disabled}
            inputProps={{ readOnly: disabled }}
            fullWidth
            helperText={
              !!error && (
                <Typography
                  component="span"
                  sx={{
                    display: 'inline',
                    float: 'left',
                    fontFamily: 'Prelo-Book, sans-serif',
                    fontSize: '.875rem',
                    fontWeight: 400,
                    color: tokens.colors.rsmRed.secondary,
                    paddingTop: '0rem',
                  }}
                  data-testid={`Txt_${componentName}_${fieldName}Error`}>
                  {error?.message}
                </Typography>
              )
            }
          />
        )}
      />
    </Box>
  )
}

export default TextField
